import {RefObject, useCallback, useRef} from 'react';
import ReactQuill from 'react-quill';
import {applyQuillFormatCommand} from '../utils/quillVariableFormat.js';
import {focusFirstEditableElement} from '../utils/dom';
import {TextTemplateItem} from '../types/templateStructure';

interface UseWhatsAppEditorActionsParams {
    events: {
        onSave?: (payload: TextTemplateItem[] | string) => void;
    };
    segmentedValue: string;
    isCharCountExceeded: boolean;
    prepareForSave: () => TextTemplateItem[];
    clearEditing: () => void;
    freeMessage: string;
    clearAllContent: () => void;
    onFreeMessageChange: (next: string) => void;
    editorContainerRef: RefObject<HTMLDivElement | null>;
}

export function useWhatsAppEditorActions({
    events,
    segmentedValue,
    isCharCountExceeded,
    prepareForSave,
    clearEditing,
    freeMessage,
    clearAllContent,
    onFreeMessageChange,
    editorContainerRef,
}: UseWhatsAppEditorActionsParams) {
    const freeQuillRef = useRef<ReactQuill | null>(null);

    const handleFreeQuillFormat = (command: 'bold' | 'italic') => {
        if(command !== 'bold' && command !== 'italic') {
            return;
        }
        const quill = freeQuillRef.current?.getEditor();
        if(!quill) {
            return;
        }
        if(!applyQuillFormatCommand(quill, command)) {
            quill.focus();
        }
    };

    const handleSave = useCallback(() => {
        if(!events.onSave || isCharCountExceeded) return;
        if(segmentedValue === 'template') {
            events.onSave(prepareForSave());
        } else {
            events.onSave(freeMessage);
        }
        clearEditing();
    }, [segmentedValue, events.onSave, isCharCountExceeded, freeMessage, prepareForSave]);

    const handleClearAll = useCallback(() => {
        if(segmentedValue === 'template') {
            clearAllContent();
        } else {
            onFreeMessageChange('');
        }

        requestAnimationFrame(() => {
            if(segmentedValue === 'template') {
                focusFirstEditableElement(editorContainerRef.current ?? document.body);
            } else {
                freeQuillRef.current?.getEditor()?.focus();
            }
        });
    }, [segmentedValue, clearAllContent, onFreeMessageChange, editorContainerRef]);

    return {
        freeQuillRef,
        handleFreeQuillFormat,
        handleSave,
        handleClearAll,
    };
}
